import { memo, useCallback, useMemo, useState } from 'react';

import { Link, useNavigate, useLocation } from 'react-router-dom';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MenuIcon from '@mui/icons-material/Menu';

import { PageContextProps, useApp } from '../../contextProviders/AppProvider';
import { useData } from '../../contextProviders/DataProvider';
import { useOnlineStatus } from '../../utils/useOnlineStatus';

import GratiIcon from './GratiIcon';
import GratiSettingsMenu from './GratiSettingsMenu';

interface GratiAppBarProps {
  sx?: SxProps<Theme>;
  isTopLevelPage?: boolean;
}

const NavMenu = memo(
  ({
    anchorElNav,
    eligiblePages,
    handleCloseNavMenu,
  }: {
    anchorElNav: HTMLElement | null;
    eligiblePages: PageContextProps[];
    handleCloseNavMenu: () => void;
  }) => {
    if (eligiblePages.length === 0) return null;

    return (
      <Menu
        id="menu-appbar"
        anchorEl={anchorElNav}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={Boolean(anchorElNav)}
        onClose={handleCloseNavMenu}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {eligiblePages.map((page) => (
          <MenuItem component={Link} key={page.name} to={page.to} onClick={handleCloseNavMenu}>
            <Typography variant="labelLarge" sx={{ textAlign: 'center' }}>
              {page.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    );
  }
);

NavMenu.displayName = 'NavMenu';

const GratiAppBar = memo((props: GratiAppBarProps) => {
  const { sx, isTopLevelPage } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { actions, getPageTitle, isHomePage, pageContexts, isSaving } = useApp();
  const { userState } = useData();
  const { isEmulator } = useOnlineStatus();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const isMenuItemDisplayed = useCallback(
    (page: PageContextProps) => {
      return (
        (page.showLogin && userState.status === 'ready') &&
        (page.showLogout && userState.status !== 'ready')
      );
    },
    [userState.status]
  );

  const eligiblePages = useMemo(() => pageContexts.filter(isMenuItemDisplayed), [pageContexts, isMenuItemDisplayed]);

  const isNavMenuDisplayed = useCallback(() => {
    return isTopLevelPage && eligiblePages.length > 0;
  }, [isTopLevelPage, eligiblePages.length]);

  const handleOpenNavMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  }, []);

  const handleCloseNavMenu = useCallback(() => {
    setAnchorElNav(null);
  }, []);

  const handleBackClick = useCallback(() => {
    if (isHomePage) {
      navigate('/');
    } else if (userState.status === 'ready' && location.state?.fromNavRail) {
      navigate('/');
    } else if (userState.status === 'ready') {
      navigate(-1);
    } else {
      // For logged-out users, go back to home page
      navigate('/');
    }
  }, [userState.status, isHomePage, navigate, location.state]);

  return (
    <AppBar 
      position="fixed" 
      className={isEmulator ? 'emulator-mode' : ''}
      sx={[
        isEmulator && {
          backgroundColor: 'emulator.main',
          '& .MuiToolbar-root': {
            backgroundColor: 'emulator.main',
          }
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      <Toolbar disableGutters>
        {/* Menu or Back Button */}
        {(eligiblePages.length > 0 && isNavMenuDisplayed()) || !isTopLevelPage ? (
          <Box className="NavMenu" sx={{ flexGrow: isNavMenuDisplayed() ? 1 : 0, display: { xs: 'flex' } }}>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={isNavMenuDisplayed() ? handleOpenNavMenu : handleBackClick}
              edge="start"
              className="AppBarIcon"
              sx={{ display: { xs: 'flex', md: isHomePage ? 'none' : 'flex' } }}
            >
              {isTopLevelPage ? <MenuIcon /> : <ArrowBackIcon />}
            </IconButton>
            <NavMenu anchorElNav={anchorElNav} eligiblePages={eligiblePages} handleCloseNavMenu={handleCloseNavMenu} />
          </Box>
        ) : userState.status === 'ready' ? (
          <Box sx={{ flexGrow: 1 }} />
        ) : (
          isTopLevelPage &&
          userState.status === 'logged_out' && (
            <IconButton className="AppBarIcon">
              <GratiIcon className="AppBarIcon" />
              <Typography variant="brandMedium" sx={{ pl: '1rem' }}>
                Gratisports
              </Typography>
            </IconButton>
          )
        )}

        {/* Title */}
        {(userState.status === 'ready' || !isTopLevelPage) && (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              ...(isTopLevelPage && {
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }),
            }}
            className="Headline"
          >
            {isTopLevelPage && userState.status === 'ready' && (
              <IconButton className="AppBarIcon">
                <GratiIcon className="AppBarIcon" />
              </IconButton>
            )}
            <Typography
              variant={isHomePage ? 'brandMedium' : 'titleMedium'}
              sx={{
                display: { xs: 'flex' },
                flexGrow: 0,
              }}
            >
              {getPageTitle()}
            </Typography>
          </Stack>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Right side items */}
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          {isSaving && (
            <CloudUploadIcon sx={{ mr: 1, color: 'action.active' }} />
          )}
          {actions.map((action) =>
            action.variant && action.variant !== 'icon' ? (
              <Button
                key={action.name}
                onClick={action.handleClick}
                variant={action.variant}
                size="small"
                sx={{ alignItems: 'center', ml: 1 }}
                startIcon={action.icon && <SvgIcon component={action.icon} />}
              >
                {action.name}
              </Button>
            ) : (
              <IconButton
                key={action.name}
                onClick={action.handleClick}
                className="AppBarIcon"
                sx={{ alignItems: 'center' }}
              >
                {action.icon && <SvgIcon component={action.icon} />}
              </IconButton>
            )
          )}
        </Stack>
        {userState.status === 'ready' && <GratiSettingsMenu sx={{ flexGrow: 0 }} />}
      </Toolbar>
    </AppBar>
  );
});

GratiAppBar.displayName = 'GratiAppBar';

export default GratiAppBar;
